import {ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {KeyValueObj} from '../../../../../../../generated/generated-core.model';
import {AssignmentResult} from '../../../../../mx-assignment-grid/services/mx-assignment-grid';
import {MxAssignmentGridDialogService} from '../../../../../mx-assignment-grid/services/mx-assignment-grid-dialog.service';
import {AssignmentSelectionType} from '../../../../../mx-grid/services/selection-type.model';
import {MxReportViewInputFilterDefault} from '../../../../../mx-report-view/services/mx-report-view-customizer-event';
import {MxFormErrorMessageService} from '../../../../services/mx-form-error-message.service';
import {MxFieldConfig} from '../../services/mx-field-customizer.service';
import {MxField} from '../../services/mxField';
import {MX_TRANSLATE_SERVICE_TOKEN, MxTranslateService} from '../../../../../../shared/services/mx-translate.service';

@Component({
    selector: 'mx-assignment-field',
    standalone: false,
    templateUrl: './mx-assignment-field.component.html',
    styleUrls: ['./mx-assignment-field.component.scss']
})
export class MxAssignmentFieldComponent extends MxField implements OnInit, OnDestroy {

    @Input() componentName: string;
    @Input() reportName: string;
    @Input() selectionMode: AssignmentSelectionType;
    @Input() filterDefaults: MxReportViewInputFilterDefault[];
    @Input() fieldConfig: MxFieldConfig;
    @Output() onAssign: EventEmitter<Array<string | number> | string> = new EventEmitter();

    private _destroy$$: Subject<null> = new Subject<null>();
    private assignedItems: Array<any> = [];
    private assignedItemsKeys: Array<string | number> | string;
    private entryFields: Array<KeyValueObj>;

    public displayValue: string;

    public get assignmentFormControl(): AbstractControl {
        return this.form.controls[this.name];
    }

    constructor(public errorService: MxFormErrorMessageService,
                protected cdRef: ChangeDetectorRef,
                private assignmentGridDialogService: MxAssignmentGridDialogService,
                @Inject(MX_TRANSLATE_SERVICE_TOKEN) protected translateService: MxTranslateService) {
        super(errorService, translateService);
    }

    ngOnInit(): void {
        if (this.fieldConfig && this.fieldConfig.assignmentConfig && this.fieldConfig.assignmentConfig.selectionMode) {
            this.selectionMode = this.fieldConfig.assignmentConfig.selectionMode;
        }
        let initValue = this.assignmentFormControl.value;
        let isInitValueSet: boolean = false;
        if (!this.selectionMode) {
            this.selectionMode = 'single';
        }
        if (this.selectionMode === 'multi') {
            if (Array.isArray(initValue) && initValue.length > 0) {
                this.assignedItems = initValue;
                this.assignedItemsKeys = initValue.map(item => item.id);
                this.entryFields = [{key: '', value: 'Selected ' + initValue.length + ' items', dependOn: null}];
                this.assignmentFormControl.setValue(this.assignedItemsKeys.join(','));
            } else if (initValue && initValue.length > 0) {
                let assignedItemsKeys: Array<any> = initValue.split(',').map(item => {
                    return {id: isNaN(+item) ? item : Number(item)};
                });
                this.assignedItems = assignedItemsKeys;
                this.assignedItemsKeys = assignedItemsKeys.map(item => item.id);
                this.entryFields = [{key: '', value: 'Selected ' + this.assignedItemsKeys.length + ' items', dependOn: null}];
                this.assignmentFormControl.setValue(this.assignedItemsKeys.join(','));
            } else {
                this.assignedItemsKeys = null;
                this.assignmentFormControl.reset();
            }
            this.assignmentFormControl
                .valueChanges
                .pipe(
                    takeUntil(this._destroy$$),
                    distinctUntilChanged()
                )
                .subscribe((value) => {
                    if (!value) {
                        this.resetAssignmentData();
                    }
                    if (isInitValueSet) {
                        this.refreshAssignmentControl();
                    }
                });
        } else {
            this.assignmentFormControl
                .valueChanges
                .pipe(
                    takeUntil(this._destroy$$),
                    distinctUntilChanged()
                )
                .subscribe(value => {
                    if (value) {
                        this.assignedItemsKeys = [value.key];
                        this.assignedItems = [value];
                    } else {
                        this.resetAssignmentData();
                    }
                    if (!isInitValueSet) {
                        isInitValueSet = true;
                        this.generateValue();
                    } else {
                        this.refreshAssignmentControl();
                    }
                });
            if (initValue) {
                this.assignmentFormControl.setValue(initValue);
            }
        }
        this.generateValue();
    }

    onAssignmentFieldClick(): void {
        if (!this.reportName && this.fieldConfig && this.fieldConfig.assignmentConfig && this.fieldConfig.assignmentConfig.reportName) {
            this.reportName = this.fieldConfig.assignmentConfig.reportName;
        }
        if (!this.filterDefaults && this.fieldConfig && this.fieldConfig.assignmentConfig
            && this.fieldConfig.assignmentConfig.filterDefaults) {
            this.filterDefaults = this.fieldConfig.assignmentConfig.filterDefaults;
        }
        this.assignmentGridDialogService.openAssignmentDialog({
            componentName: this.componentName,
            assignedItems: this.assignedItems,
            reportName: this.reportName,
            filterDefaults: this.filterDefaults,
            selectionMode: this.selectionMode,
            disableRowFunc: this.fieldConfig && this.fieldConfig.assignmentConfig && this.fieldConfig.assignmentConfig.disableRowFunc,
            callbackAction: (result: AssignmentResult) => {
                if (Array.isArray(result.assignedItems)) {
                    this.assignedItems = result.assignedItems;
                    this.setAssignmentFieldValue(result);
                }
                this.refreshAssignmentControl();
            }
        });
    }

    private setAssignmentFieldValue(result: AssignmentResult): void {
        let assignedItemsKeys = [];

        if (this.selectionMode === 'multi') {
            if (Array.isArray(result.assignedItemsKeys)) {
                assignedItemsKeys = result.assignedItemsKeys;
            }
            if (assignedItemsKeys.length > 0) {
                this.assignmentFormControl.setValue(assignedItemsKeys.join(','));
            } else {
                this.assignmentFormControl.reset();
            }
            this.entryFields = result.entryFields;
        } else if (result.entryFields.length > 0) {
            assignedItemsKeys = [result.entryFields[0].key];
            this.assignedItems = [result.entryFields[0]];
            this.assignmentFormControl.setValue(result.entryFields[0]);
        }

        this.assignedItemsKeys = assignedItemsKeys;
        this.onAssign.emit(this.assignedItemsKeys);
    }

    private generateValue(): void {
        this.displayValue = undefined;
        this.cdRef.detectChanges();
        if (this.selectionMode === 'multi') {
            if (this.assignedItems.length > 0 && this.entryFields && this.entryFields.length > 0) {
                this.displayValue = this.entryFields.map(value => value.value).join(', ');
            } else {
                this.displayValue = 'Select...';
            }
            this.cdRef.detectChanges();
        } else {
            this.displayValue = this.assignedItems.length > 0 ? this.assignedItems[0].value : '';
            this.cdRef.detectChanges();
        }
    }

    private resetAssignmentData(): void {
        this.assignedItemsKeys = [];
        this.assignedItems = [];
        this.entryFields = [];
        this.generateValue();
    }

    private refreshAssignmentControl(): void {
        this.assignmentFormControl.markAsTouched();
        this.assignmentFormControl.markAsDirty();
        this.generateValue();
    }

    public resetValue(): void {
        this.assignmentFormControl.reset();
        this.refreshAssignmentControl();
        this.resetAssignmentData();
        this.onAssign.emit(this.assignedItemsKeys);
    }

    ngOnDestroy(): void {
        this._destroy$$.next(null);
    }
}
